import React, { useState } from "react";
import {
  useGetFanOutArtifactsQuery,
  useGetFanOutScopeDiffQuery,
} from "../../../app/api/console/agenticRunsApiSlice";
import { useGetAgentMemoryFileQuery } from "../../../app/api/console/agentMemoryApiSlice";
import { ToolPayloadPanel } from "./ToolCallDetails";

/**
 * Per-fan-out S3 artifact panel: inventory grouped by pipeline phase, the
 * precise scope↔items reconciliation, and an inline JSON viewer.
 *
 * Grouped by the phase that produced each file rather than by S3 directory —
 * an operator re-reading a trace thinks "what was asked / what went out /
 * what came back", not "which prefix is this under".
 *
 * Reading a file needs no dedicated endpoint: GET /agent-memory/file already
 * serves any key in the agentic bucket behind the same superuser guard.
 */

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_CONFIG = {
  ok: { label: "chain complete", cls: "bg-emerald-50 text-emerald-700" },
  under_dispatch: {
    label: "scope urls never dispatched",
    cls: "bg-amber-50 text-amber-800",
  },
  partial_return: { label: "workers missing", cls: "bg-amber-50 text-amber-800" },
  in_flight: { label: "in flight", cls: "bg-blue-50 text-blue-700" },
  no_scope: { label: "no scope input", cls: "bg-gray-100 text-gray-600" },
  unknown: { label: "unknown", cls: "bg-gray-100 text-gray-600" },
};

function Reconciliation({ rec, onShowDiff, diffOpen }) {
  if (!rec) return null;
  const cfg = STATUS_CONFIG[rec.status] || STATUS_CONFIG.unknown;
  const cells = rec.dispatch?.max_cells_per_url || 0;
  return (
    <div className='flex flex-wrap items-center gap-2 mb-3 text-xs'>
      {rec.scope?.url_count > 0 && (
        <>
          <span className='text-gray-500'>
            scope{" "}
            <span className='font-medium text-gray-800 tabular-nums'>
              {rec.scope.url_count}
            </span>{" "}
            urls
          </span>
          <span className='text-gray-300'>→</span>
        </>
      )}
      <span className='text-gray-500'>
        dispatch{" "}
        <span className='font-medium text-gray-800 tabular-nums'>
          {rec.dispatch?.item_count ?? "—"}
        </span>
        {cells > 1 && (
          <span className='text-gray-400'>
            {" "}
            ({rec.dispatch.url_count} urls × up to {cells} cells)
          </span>
        )}
      </span>
      <span className='text-gray-300'>→</span>
      <span className='text-gray-500'>
        returned{" "}
        <span className='font-medium text-gray-800 tabular-nums'>
          {rec.results?.returned ?? "—"}
        </span>
      </span>
      <span className={`px-1.5 py-0.5 rounded font-medium ${cfg.cls}`}>
        {cfg.label}
      </span>
      {rec.scope?.url_count > 0 && (
        <button
          type='button'
          onClick={onShowDiff}
          className='ml-auto text-indigo-600 hover:text-indigo-800 font-medium'>
          {diffOpen ? "hide diff" : "diff scope ↔ items"}
        </button>
      )}
    </div>
  );
}

function ScopeDiff({ env, runId, fanOutId }) {
  const { data, isLoading, error } = useGetFanOutScopeDiffQuery({
    env,
    runId,
    fanOutId,
  });

  if (isLoading)
    return <div className='px-3 py-2 text-xs text-gray-400'>Computing diff…</div>;
  if (error)
    return (
      <div className='px-3 py-2 text-xs text-red-600'>
        {error?.data?.detail || "diff failed"}
      </div>
    );

  const missing = data?.missing || [];
  const extra = data?.extra || [];

  return (
    <div className='border border-gray-300 rounded-lg overflow-hidden mb-3'>
      <div className='flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200'>
        <span className='text-xs font-medium text-gray-800'>
          diff scope ↔ items.json
        </span>
        <span className='text-[11px] text-gray-500 font-mono'>
          {data?.missing_total || 0} missing · {data?.extra_total || 0} extra ·{" "}
          {data?.common_count || 0} common
        </span>
      </div>
      <div className='px-3 py-2 font-mono text-[11px] leading-relaxed max-h-[260px] overflow-y-auto'>
        {missing.length === 0 && extra.length === 0 ? (
          <div className='text-emerald-700'>
            Every scope url is covered by at least one dispatched item.
          </div>
        ) : (
          <>
            {missing.map((u) => (
              <div key={`m-${u}`} className='text-red-700 break-all'>
                − {u}
              </div>
            ))}
            {extra.map((u) => (
              <div key={`e-${u}`} className='text-blue-700 break-all'>
                + {u}
              </div>
            ))}
            {data?.truncated && (
              <div className='text-gray-400 mt-1'>
                … truncated, use the raw files for the full list
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function FileViewer({ env, fileKey, onClose }) {
  const { data, isLoading, error } = useGetAgentMemoryFileQuery({
    env,
    key: fileKey,
  });

  return (
    <div className='border border-gray-300 rounded-lg overflow-hidden mb-3'>
      <div className='flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200'>
        <span className='text-[11px] font-mono text-gray-700 truncate flex-1'>
          {fileKey}
        </span>
        {data?.size != null && (
          <span className='text-[11px] text-gray-400'>
            {formatBytes(data.size)}
          </span>
        )}
        <button
          type='button'
          onClick={onClose}
          className='text-[11px] text-gray-500 hover:text-gray-800'>
          close
        </button>
      </div>
      <div className='px-3 py-2'>
        {isLoading && <div className='text-xs text-gray-400'>Loading…</div>}
        {error && (
          <div className='text-xs text-red-600'>
            {error?.status === 404
              ? "not found"
              : error?.data?.detail || "read failed"}
          </div>
        )}
        {data?.binary && (
          <div className='text-xs text-gray-500'>binary file — not shown</div>
        )}
        {/* Reuses the timeline's structured viewer rather than a bespoke one:
            it already handles syntax highlighting, truncated-JSON repair,
            deep-parsing of nested JSON strings, per-string expansion and a
            copy button — and falls back to plain text for non-JSON. */}
        {data?.text != null && !data.binary && (
          <ToolPayloadPanel value={data.text} showHeader={false} />
        )}
      </div>
    </div>
  );
}

function FileRow({ file, bucket, onView, active }) {
  const [copied, setCopied] = useState(false);
  const s3Uri = `s3://${bucket}/${file.key}`;

  return (
    <div
      className={`flex items-center gap-2 px-1 py-1.5 border-t border-gray-100 text-xs ${
        active ? "bg-indigo-50/60" : ""
      }`}>
      <span
        className='flex-1 font-mono text-gray-700 truncate min-w-0'
        title={file.rel}>
        {file.rel}
      </span>
      {file.label !== file.rel && (
        <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0'>
          {file.label}
        </span>
      )}
      {file.count != null && (
        <span className='text-[11px] text-gray-500 font-mono shrink-0 tabular-nums'>
          {file.count} {file.count_label}
        </span>
      )}
      <span className='text-[11px] text-gray-400 shrink-0 w-14 text-right'>
        {formatBytes(file.size)}
      </span>
      <button
        type='button'
        onClick={() => onView(file.key)}
        className='text-[11px] font-medium text-indigo-600 hover:text-indigo-800 shrink-0'>
        {active ? "viewing" : "view"}
      </button>
      <button
        type='button'
        title={s3Uri}
        onClick={() => {
          navigator.clipboard?.writeText(s3Uri);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
        className='text-[11px] text-gray-400 hover:text-gray-700 shrink-0 w-10 text-left'>
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}

export default function FanOutArtifacts({ env, runId, fanOutId }) {
  const [viewing, setViewing] = useState(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const { data, isLoading, error } = useGetFanOutArtifactsQuery({
    env,
    runId,
    fanOutId,
  });

  if (isLoading)
    return (
      <div className='px-4 py-3 text-xs text-gray-400'>Loading artifacts…</div>
    );
  if (error)
    return (
      <div className='px-4 py-3 text-xs text-red-600'>
        {error?.data?.detail || error?.error || "Failed to load artifacts"}
      </div>
    );

  const bucket = `zenyt-${env}-agentic`;
  const phases = data?.phases || [];

  return (
    <div className='px-4 py-3 bg-gray-50/70 border-t border-gray-100'>
      <Reconciliation
        rec={data?.reconciliation}
        diffOpen={diffOpen}
        onShowDiff={() => setDiffOpen((v) => !v)}
      />

      {diffOpen && (
        <ScopeDiff env={env} runId={runId} fanOutId={fanOutId} />
      )}
      {viewing && (
        <FileViewer
          env={env}
          fileKey={viewing}
          onClose={() => setViewing(null)}
        />
      )}

      {phases.length === 0 ? (
        <div className='text-xs text-gray-400'>
          No artifacts found under this fan-out's S3 prefix.
        </div>
      ) : (
        <div className='space-y-3'>
          {phases.map((phase) => (
            <div key={phase.id}>
              <div className='flex items-baseline gap-2'>
                <span className='text-[11px] uppercase tracking-wide text-gray-500 font-semibold'>
                  {phase.label}
                </span>
                <span className='text-[11px] text-gray-400'>{phase.desc}</span>
              </div>
              {phase.files.map((f) => (
                <FileRow
                  key={f.key}
                  file={f}
                  bucket={bucket}
                  active={viewing === f.key}
                  onView={(k) => setViewing((cur) => (cur === k ? null : k))}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
